/** What the AI is entitled to assume about an army it has never seen.
 *
 * A conquest decision needs a defender to weigh, and the defender behind a town
 * wall is exactly the thing I9 forbids looking at. The honest substitute is not
 * a guess at a number - a closed-form "their army is probably worth N" is the
 * same mistake as a closed-form battle estimator, one layer up - but a *plausible
 * army*, assembled from facts the observation genuinely carries, which is then
 * fought in the real kernel by the same probe a wandering monster goes through.
 * One estimator, and it is the game.
 *
 * Two questions have to be answered to build one: how big, and what of.
 *
 * **How big** is anchored on the AI itself, not on the calendar. An estimate
 * that multiplies a faction's weekly growth by the week number says a defender
 * on day 200 has twenty-eight weeks of dwelling output standing in his hall,
 * which no player and no computer has ever had, and an AI that believes it
 * never attacks anything again. What the AI does know honestly is what *it* has
 * managed by the same day under the same rules, and the public scoreboard -
 * `ObservedOpponent` carries town and hero counts for every player, fog or no
 * fog, because a scoreboard is not a scouting report. A rival with twice the
 * towns is presumed to have twice the army.
 *
 * One thing the hero count is deliberately *not* used for: dividing a kingdom
 * evenly over the heroes it holds. It is the tempting reading of the scoreboard
 * and it is how an AI gets its main army killed - four heroes read as four
 * quarter-armies when in truth three are scouts and the fourth is everything he
 * has. An enemy hero is instead presumed to carry the share of his kingdom that
 * ours carries of ours, scaled by the one thing the map does show of him.
 *
 * **What of** comes from the town's faction, which `observe` publishes for any
 * town whose cell has ever been explored, because a town is a landmark and its
 * silhouette names its faction. The ladder fixes the shape - what tiers, in what
 * proportion - and the size above fixes the scale.
 *
 * Deliberately absent: any difficulty term. How strong the enemy probably is is
 * a matter of fact and every level gets the same answer. How much of a bloody
 * win is worth taking is a matter of skill, and that lives in `aggressionBp`
 * and `attackRatioFp` where a designer can read it.
 */

import { fortLevelOf, makeCtx } from "@game/core";
import type { ArmySlot, CreatureDefId, FactionId } from "@game/core";
import { TIER_WEIGHT_BP_PER_TIER } from "./evaluate.js";
import type { KnownEnemy, Shadow } from "./shadow.js";
import { fieldAndGarrison } from "./strategy.js";

/** Slots a `StartBattleInput` side has; the ladder is truncated to fit. */
const ARMY_SLOTS = 7;

/**
 * Share of a rival's presumed whole army the AI expects to have to beat to take
 * his capital.
 *
 * Near parity on purpose. A capital is not a chest with a lock on it: the hero
 * who is not standing in it is a day's ride away, the garrison grows every week
 * whether or not anybody is watching, and the walls and turrets a siege adds are
 * not in the probe at all. Priced at forty per cent this read as "their capital
 * is a third of what I have", every AI marched its starting hero at the enemy
 * on day one, and whichever moved first won by day three or four - a decided
 * game, and not a game. At ninety it takes a real advantage to move, which is
 * when a human would move too.
 */
export const TOWN_DEFENCE_BP = 9000;
/**
 * How that share is spread. A kingdom's army sits in the places that can hold
 * one - its towns and its heroes - and the public scoreboard names both counts
 * for every player, fog or no fog.
 *
 * Without this divisor `TOWN_DEFENCE_BP` read as "ninety per cent of everything
 * that rival owns is standing in *this* hall", which is only true of a kingdom
 * down to one seat. Against a rival holding three it presumed a defender three
 * times the real one, and the measured consequence was the stall this round
 * exists to fix: on seed 12161 at day 201 the marching hero carried 455k and
 * the town in front of him was priced at 2668k, so the probe said "lose" every
 * day for two hundred days.
 *
 * Towns *and* heroes, because both can hold an army and the scoreboard names
 * both. Dividing by towns alone was tried and measured: it leaves the opening
 * exactly where round 9 left it, and it costs decided games in the middle,
 * where a rival's field army is in his heroes' hands rather than in his halls -
 * on the two ten-seed 48x48 four-player sweeps it decided 14 of 20 against 17
 * of 20 for this.
 *
 * The endgame falls out of the same line rather than needing a rule of its own:
 * as a rival is ground down to his last town and his last hero, the divisor
 * falls to two and the presumed defence rises to meet what is really there.
 */
export const HOLDER_FLOOR = 1;
/**
 * Weeks of its own dwellings' growth a town nobody owns is presumed to be
 * guarded by.
 *
 * A neutral town is not a kingdom and must not be priced as one. Scored through
 * the rival yardstick - which is what happens when a town with no flag falls
 * through to "the worst plausible rival" - a 20k neutral garrison read as 3006k
 * on seed 12161, and no computer ever took one: three of the map's neutral
 * towns were still neutral on day 300 while four kingdoms circled each other.
 * The honest anchor is the town itself, because its garrison is placed once by
 * the generator and never grows: what a hall of that size could field.
 *
 * Two weeks rather than one, and the difference is a hero's life. Real neutral
 * garrisons on generated 48x48 maps measure 2k to 21k, sized by how far the
 * town sits from the nearest start, and one week of a four-tier ladder is under
 * the top of that range - so at one week the AI walked its opening hero into a
 * neutral town on day one and lost it, on both sides, measured directly on
 * 36x36 seed 40595. Across the two ten-seed 48x48 four-player sweeps, one week
 * decided 14 of 20 and two decided 17 of 20.
 *
 * Three was tried and measured and is not here, and the measurement is worth
 * keeping because the argument for it is a good one. Two weeks of a four-tier
 * ladder comes out at 5008 against halls that really hold 7849 and 8305 - about
 * two thirds of the truth - which is what 48x48 seed 72271 costs a kingdom:
 * three heroes walked into halls of that size in two days. But three weeks does
 * not buy those heroes back. It makes the AI skip the neutral halls it *can*
 * take, which is how a kingdom on a small map has nothing to do on day two
 * except march, and the 48x48 two-player sweep answered with two four-day games
 * where two weeks gives thirteen and twenty-eight (seeds 32676 and 56433,
 * measured with the rest of this round held fixed). The under-pricing is real
 * and the fix for it is not this number.
 */
export const NEUTRAL_TOWN_WEEKS = 2;
/**
 * What the probe used to be unable to see, and the reason a town was not priced
 * like a field.
 *
 * `probeArmies` fought an open battlefield: no wall, no gate, no turret. A
 * siege has all three, so a presumed defender the probe beat at parity was a
 * siege the AI lost - and this basis-point margin was where that was priced,
 * because the probe could not be told about the wall.
 *
 * It can now: `presumedFort` below says how well the town is fortified and
 * `probeArmies` fights the real siege behind that wall, through the same kernel
 * that builds the real one. So this stays at 1.0 and is now a genuine no-op
 * rather than a knob deliberately parked at neutral - the caution it stood for
 * is in the battle instead of in a multiplier over it, which is the shape the
 * rest of this file argues for. Left in place because a designer who wants the
 * AI to respect a specific map's towns more than the rules say it should has
 * nowhere else to write that down.
 */
export const TOWN_FORTIFICATION_BP = 10000;
/**
 * How well a town nobody owns is presumed to be fortified.
 *
 * Every town this generator lays - the starts and the neutral halls alike -
 * comes with a Fort and nothing above it (measured across the 48x48, 72x72 and
 * four-player starts: every town on the board is fort level 1 on day one). A
 * hall with no owner has nobody to build the Citadel, so it is still a Fort
 * whenever we get there, and that is a fact about the rules rather than a guess
 * about a player.
 */
export const NEUTRAL_FORT_LEVEL = 1;

const OWN_FORT = new WeakMap<Shadow, number>();

/**
 * The wall the AI expects to find in front of a town, for `probeArmies`.
 *
 * A rival is presumed to have fortified as we have. That is the same symmetric
 * assumption the rest of this file is built on - `PARITY_BP` for how big his
 * army is, our own faction for what it is made of - and it is the honest one:
 * an AI that reads the enemy's fort level off `GameState` is reading through
 * the fog, and one that assumes an open town walks its kingdom into a Castle.
 * Measured on the AI's own build order, both computers upgrade the starting
 * Fort to a Castle inside the first week, so "as well fortified as our best
 * hall" is not a pessimistic reading of a rival, it is a description of one.
 *
 * Cached per shadow: it is a property of our own towns, which do not change
 * inside one decision, and `makeCtx` is not free.
 */
export function presumedFort(sh: Shadow, e: KnownEnemy): number {
  if (e.kind !== "town") return 0;
  if (e.owner === null) return NEUTRAL_FORT_LEVEL;
  const hit = OWN_FORT.get(sh);
  if (hit !== undefined) return hit;
  const c = makeCtx(sh.state, sh.content);
  let best = NEUTRAL_FORT_LEVEL;
  for (const t of sh.obs.towns) {
    if (t.detail === null) continue;
    const f = fortLevelOf(c, t.detail);
    if (f > best) best = f;
  }
  OWN_FORT.set(sh, best);
  return best;
}
/** The same, for the army one enemy hero is presumed to be carrying. */
export const HERO_FIELD_BP = 5500;
/**
 * A rival about whom nothing at all is known is presumed to be doing as well as
 * we are. Not better: an AI that assumes the worst never leaves home, and the
 * cost of being wrong here is one lost army, not a lost game.
 */
export const PARITY_BP = 10000;
/**
 * What one hero is worth against one town when reading a rival's earning power
 * off the scoreboard.
 *
 * Towns alone are not the economy. A hero is what flags a mine, opens a chest
 * and clears a bank, and a kingdom with no hero on the board has been earning
 * nothing but its own hall's income for as long as that has been true. Reading
 * town counts alone, a player reduced to one town and no heroes still priced
 * out at half of everything the leader owned - and on seed 12161 that is
 * exactly the position that ran to the 300-day ceiling: `p4` held one town and
 * no hero from day 20, his real garrison was 28k, and both survivors presumed
 * he was defending it with something over a million and left him alone.
 *
 * Counted as one town's worth, which is roughly what a hero collects.
 */
export const HERO_EARN_WEIGHT = 1;
/** Nobody defends a town with literally nothing, however poor they are. */
export const PRESUMED_FLOOR = 400;

/**
 * The base creature of each tier a faction can field, tier 1 first.
 *
 * Upgrades are skipped: an AI that assumes every enemy dwelling is upgraded
 * declines every fight, and the difference is a safety margin that belongs in
 * `attackRatioFp` where a designer can see it, not buried in a ladder.
 */
const LADDERS = new WeakMap<object, Map<string, readonly CreatureDefId[]>>();

function ladder(sh: Shadow, faction: FactionId): readonly CreatureDefId[] {
  const cached = LADDERS.get(sh.content);
  const hit = cached?.get(faction as string);
  if (hit !== undefined) return hit;
  const byTier = new Map<number, CreatureDefId>();
  for (const id of [...sh.content.creatures.keys()].sort()) {
    const c = sh.content.creatures.get(id);
    if (c === undefined || c.faction !== faction || c.upgraded) continue;
    if (!byTier.has(c.tier)) byTier.set(c.tier, c.id);
  }
  const out: CreatureDefId[] = [];
  for (let t = 1; t <= ARMY_SLOTS; t++) {
    const id = byTier.get(t);
    if (id !== undefined) out.push(id);
  }
  const table = cached ?? new Map<string, readonly CreatureDefId[]>();
  table.set(faction as string, out);
  LADDERS.set(sh.content, table);
  return out;
}

/**
 * The faction to assume. A town names its own; a hero borrows it from a town of
 * his that we have seen, and failing that from ours - the symmetric assumption,
 * and the only one left when nothing of theirs has ever been looked at.
 */
function factionFor(sh: Shadow, e: KnownEnemy): FactionId | null {
  if (e.kind === "town" && e.faction !== null) return e.faction as FactionId;
  let best: string | null = null;
  for (const other of sh.enemies) {
    if (other.kind !== "town" || other.faction === null) continue;
    if (e.owner !== null && other.owner !== e.owner) continue;
    if (best === null || other.faction < best) best = other.faction;
  }
  if (best !== null) return best as FactionId;
  for (const t of sh.obs.towns) if (t.detail !== null) return t.detail.faction;
  return null;
}

/**
 * What the public scoreboard says one rival holds. With no named owner the
 * worst plausible rival is the yardstick - the one holding the most towns,
 * because a town whose flag we cannot see could be his.
 */
interface Scoreboard {
  readonly towns: number;
  readonly heroes: number;
}

function scoreboardOf(sh: Shadow, owner: string | null): Scoreboard {
  let best: Scoreboard = { towns: 0, heroes: 0 };
  for (const o of sh.obs.opponents) {
    if (o.defeated) continue;
    if (owner !== null && (o.id as string) !== owner) continue;
    if (owner !== null) return { towns: o.townCount, heroes: o.heroCount };
    if (o.townCount > best.towns) best = { towns: o.townCount, heroes: o.heroCount };
  }
  return best;
}

/** Places that rival can be keeping an army: his towns and his heroes. */
function holdersOf(sh: Shadow, owner: string | null): number {
  const sb = scoreboardOf(sh, owner);
  return Math.max(HOLDER_FLOOR, Math.max(1, sb.towns) + sb.heroes);
}

/** Towns plus heroes, the two things on the scoreboard that earn. */
function earningPower(towns: number, heroes: number): number {
  return Math.max(1, towns + heroes * HERO_EARN_WEIGHT);
}

/** What one rival is presumed to be able to put in the field, all told. */
export function presumedKingdomStrength(sh: Shadow, owner: string | null): number {
  const mine = fieldAndGarrison(sh);
  let ownTowns = 0;
  for (const t of sh.obs.towns) if (t.detail !== null) ownTowns++;
  let ownHeroes = 0;
  for (const h of sh.obs.heroes) if (h.owner === sh.player) ownHeroes++;
  const sb = scoreboardOf(sh, owner);
  const ratio = earningPower(sb.towns, sb.heroes) * 10000
    / earningPower(ownTowns, ownHeroes);
  return Math.max(PRESUMED_FLOOR, Math.floor((mine * PARITY_BP * ratio) / 100000000));
}

/**
 * An army of `faction` worth about `target`, shaped like a week of that
 * faction's dwellings. The shape is what makes the probe informative - seven
 * tiers with the top of the ladder in it fights nothing like one enormous stack
 * of peasants worth the same gold - and the scale is what keeps the estimate
 * honest as the game goes on.
 */
interface Shape {
  readonly slots: readonly ArmySlot[];
  /** What one week of that shape is worth, in `armyStrength` currency. */
  readonly unit: number;
}

/** One week of a faction's dwellings, top tier first, capped at `tiers`. */
function weekShape(sh: Shadow, faction: FactionId, tiers: number): Shape | null {
  const ids = ladder(sh, faction);
  if (ids.length === 0) return null;
  const slots: ArmySlot[] = [];
  for (let i = ids.length - 1; i >= 0 && slots.length < ARMY_SLOTS; i--) {
    const id = ids[i]!;
    const def = sh.content.creatures.get(id);
    if (def === undefined || def.tier > tiers) continue;
    slots.push({ def: id, count: def.growth > 0 ? def.growth : 1 });
  }
  if (slots.length === 0) return null;
  // Measured in the same currency `fieldAndGarrison` reports, tier weighting
  // included, or the scale would be off by the weighting itself.
  let unit = 0;
  for (const st of slots) {
    const def = sh.content.creatures.get(st.def);
    if (def === undefined) continue;
    unit += Math.floor(
      (def.aiValue * st.count * (10000 + TIER_WEIGHT_BP_PER_TIER * def.tier)) / 10000,
    );
  }
  return unit <= 0 ? null : { slots, unit };
}

function armyWorth(
  sh: Shadow, faction: FactionId, target: number, tiers: number,
): readonly (ArmySlot | null)[] | null {
  if (target <= 0) return null;
  const built = weekShape(sh, faction, tiers);
  if (built === null) return null;
  const shape = built.slots;
  const unit = built.unit;
  // Fixed point, so a target a fifth of one week of growth still scales rather
  // than rounding every stack to nothing.
  const scaleFp = Math.max(1, Math.floor((target * 1000) / unit));

  const slots: (ArmySlot | null)[] = [null, null, null, null, null, null, null];
  for (let i = 0; i < shape.length && i < ARMY_SLOTS; i++) {
    const s = shape[i]!;
    slots[i] = { def: s.def, count: Math.max(1, Math.floor((s.count * scaleFp) / 1000)) };
  }
  return slots;
}

/** Tiers a town with this many buildings, or a hero of this level, can field. */
const clamp = (n: number, lo: number, hi: number): number => (n < lo ? lo : n > hi ? hi : n);
export const TIERS_PER_BUILDING = 3;
export const LEVELS_PER_TIER = 2;
export const UNSEEN_TIERS = 4;

/**
 * The army to fight when the AI decides whether to take this town or this hero.
 * Null when there is not enough content to assemble one, which the caller reads
 * as "no opinion" rather than as "walk in".
 */
export function presumedArmy(sh: Shadow, e: KnownEnemy): readonly (ArmySlot | null)[] | null {
  const faction = factionFor(sh, e);
  if (faction === null) return null;
  const kingdom = presumedKingdomStrength(sh, e.owner as string | null);
  if (e.kind === "town") {
    const tiers = clamp(
      e.rank > 0 ? 1 + Math.floor(e.rank / TIERS_PER_BUILDING) : UNSEEN_TIERS, 1, 7,
    );
    // A town the observation is lighting up right now and reports as owned by
    // nobody really is nobody's: `observe` publishes the live owner of a lit
    // town. It has no kingdom behind it and its garrison never grows, so it is
    // priced off its own hall - see `NEUTRAL_TOWN_WEEKS`.
    const fortified = (n: number): number =>
      Math.floor((n * TOWN_FORTIFICATION_BP) / 10000);
    const rival = fortified(
      Math.floor((kingdom * TOWN_DEFENCE_BP) / 10000 / holdersOf(sh, e.owner as string | null)),
    );
    // "Owned by nobody" is two different facts wearing one field, and telling
    // them apart is the whole of this branch.
    //
    // `knownEnemies` publishes every town on explored ground, and a town whose
    // cell has been walked past but whose flag has never been *lit* arrives
    // here with `owner: null` and `rank: 0` - no snapshot was ever taken of it.
    // In a two-player game that is usually the enemy capital, and pricing it
    // off its own hall rather than off the rival scoreboard is how an AI talks
    // itself out of the only march that ends the game. `rank > 0` is the tell
    // that a snapshot exists, which is the same tell the tier estimate above
    // already reads: we have looked at this town, and what we saw was a hall
    // with no flag on it.
    if (e.owner === null && (e.fresh || e.rank > 0)) {
      const shape = weekShape(sh, faction, tiers);
      if (shape === null) return armyWorth(sh, faction, rival, tiers);
      const hall = fortified(shape.unit * NEUTRAL_TOWN_WEEKS);
      // Lit, the anchor is the whole answer: it really is nobody's, right now.
      // Only *remembered* as unowned, two things could be true - it is still
      // the hall it was, or a rival has taken it since - and the old code
      // answered with the rival yardstick alone, which is a parity read of our
      // own kingdom divided over its holders and came back about a third of the
      // lit value. Measured on 36x36 seed 60583: halls really holding 7842 and
      // 9422 were walked into by heroes carrying 3710, twice, and the kingdom
      // that lost them lost the game on day three. The larger of the two is the
      // only reading that is safe whichever of them is true.
      return armyWorth(sh, faction, e.fresh ? hall : Math.max(hall, rival), tiers);
    }
    return armyWorth(sh, faction, rival, tiers);
  }
  // A hero is presumed to carry the same share of his kingdom that ours carries
  // of ours, scaled by the one thing the map does show of him: his level.
  // Dividing the kingdom evenly over the hero count is the tempting alternative
  // and it is how an AI gets its main army killed - a rival with four heroes
  // reads as four quarter-armies, when in truth three of them are scouts and the
  // fourth is everything he has.
  let ourBest = 0;
  for (const h of sh.obs.heroes) {
    if (h.owner !== sh.player || h.detail === null) continue;
    if (h.detail.level > ourBest) ourBest = h.detail.level;
  }
  const weight = ourBest <= 0 || e.rank <= 0
    ? 10000
    : clamp(Math.floor((e.rank * 10000) / ourBest), 2500, 15000);
  const share = Math.floor((kingdom * HERO_FIELD_BP * weight) / 100000000);
  const tiers = e.rank > 0 ? 1 + Math.floor(e.rank / LEVELS_PER_TIER) : UNSEEN_TIERS;
  return armyWorth(sh, faction, share, clamp(tiers, 1, 7));
}
